import { Form } from '@components/form/Form'
import { FormColorPicker } from '@components/form/FormColorPicker'
import { FormDropzone } from '@components/form/FormDropzone'
import { FormTextfield } from '@components/form/FormTextfield'
import { base64ToFile } from '@components/utils/base64ToFile'
import { ConfigProviderContext } from '@components/utils/config-provider'
import { ChangePrimaryColor } from '@features/creator/ChangePrimaryColor'
import { zodResolver } from '@hookform/resolvers/zod'
import CopyAll from '@mui/icons-material/CopyAll'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Collapse from '@mui/material/Collapse'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import { useNavigate } from '@tanstack/react-router'
import { useSnackbar } from 'notistack'
import { useContext, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

const formSchema = z
  .object({
    files: z
      .array(
        z.object({
          file: z.any(),
          id: z.string(),
        })
      )
      .min(1),
    companyName: z.string(),
    email: z.string(),
    phone: z.string(),
    primary: z.string(),
    secondary: z.string(),
    error: z.string(),
    warning: z.string(),
    info: z.string(),
    success: z.string(),
    backgroundPaper: z.string(),
    backgroundDefault: z.string(),
    backgroundPaperDark: z.string(),
    backgroundDefaultDark: z.string(),
  })
  .required()

type FormValues = z.infer<typeof formSchema>

export function CreatorForm() {
  const [code, setCode] = useState<string | null>(null)
  const context = useContext(ConfigProviderContext)
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()

  const loginForm = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      files:
        context?.config?.logo?.length > 0 ? [{ id: '1', file: base64ToFile(context?.config?.logo, 'logo.png') }] : [],
      ...(context?.config || {}),
    },
  })

  const onSubmit = (values: FormValues) => {
    const fileReader = new FileReader()

    fileReader.onload = (event: ProgressEvent<FileReader>) => {
      try {
        const base64 = event.target?.result
        context?.onChange?.({
          ...values,
          files: undefined,
          logo: base64 ? base64.toString() : '',
          onGenerateCode: (code) => {
            setCode(code)
          },
        })
        enqueueSnackbar('Configuration saved', { variant: 'success' })
      } catch (error) {
        console.error('error', error)
      }
    }

    fileReader.readAsDataURL(values.files?.[0]?.file)
  }

  const handleBack = () => {
    navigate({ to: '/' })
  }

  const watchFile = loginForm.watch('files')?.[0]?.file

  return (
    <>
      <Collapse in={!!code}>
        <Box
          display="grid"
          gridTemplateColumns="1fr"
          gap={2}
          sx={{ minWidth: 300 }}
        >
          <Box>
            <TextField
              fullWidth
              value={`${window.location.origin}?code=${code}`}
              slotProps={{
                input: {
                  endAdornment: (
                    <IconButton onClick={() => navigator.clipboard.writeText(`${window.location.origin}?code=${code}`)}>
                      <CopyAll />
                    </IconButton>
                  ),
                },
              }}
            />
          </Box>
          <Button
            type="button"
            variant="contained"
            color="secondary"
            onClick={handleBack}
          >
            Back to application
          </Button>
        </Box>
      </Collapse>
      <Collapse in={!code}>
        <form
          onSubmit={loginForm.handleSubmit(onSubmit)}
          method="post"
        >
          <Form {...loginForm}>
            <Box
              display="grid"
              gridTemplateColumns="1fr"
              gap={2}
              sx={{ minWidth: 300 }}
            >
              <FormDropzone<FormValues>
                label={'Logo'}
                supportedFileTypesLabel={'.png, .jpg, .jpeg'}
                name="files"
                renderPostUploadContent={(file) => (
                  <img
                    key={file.id}
                    src={URL.createObjectURL(file.file)}
                    alt={file.file.name}
                    style={{ maxWidth: '300px' }}
                  />
                )}
              />
              <Box
                display="grid"
                gridTemplateColumns="1fr 1fr"
                gap={2}
                sx={{ minWidth: 300 }}
              >
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <Divider />
                </Box>
                <FormTextfield
                  name="companyName"
                  label="Company name"
                  size="small"
                />
                <FormTextfield
                  name="email"
                  label="E-mail"
                  size="small"
                />
                <FormTextfield
                  name="phone"
                  label="Phone"
                  size="small"
                />
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <Divider />
                </Box>
                <FormColorPicker
                  name="primary"
                  label="Primary"
                  size="small"
                />
                <FormColorPicker
                  name="secondary"
                  label="Secondary"
                  size="small"
                />
                <FormColorPicker
                  name="warning"
                  label="Warning"
                  size="small"
                />
                <FormColorPicker
                  name="error"
                  label="Error"
                  size="small"
                />
                <FormColorPicker
                  name="success"
                  label="Success"
                  size="small"
                />
                <FormColorPicker
                  name="info"
                  label="Info"
                  size="small"
                />
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <Divider />
                </Box>
                <FormColorPicker
                  name="backgroundPaper"
                  label="Background element"
                  size="small"
                />
                <FormColorPicker
                  name="backgroundDefault"
                  label="Background default"
                  size="small"
                />
                <Button
                  type="button"
                  variant="contained"
                  color="secondary"
                  onClick={handleBack}
                >
                  Back to application
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={!loginForm.formState.isValid}
                >
                  Save
                </Button>
              </Box>
            </Box>
            <ChangePrimaryColor
              file={watchFile}
              onChange={(value) => loginForm.setValue('primary', value)}
            />
          </Form>
        </form>
      </Collapse>
    </>
  )
}
